/**
 * Validation pass — ensure map quality after generation.
 *
 * - Connectivity: all walkable regions reachable from each other
 * - Dead-end detection
 * - Exit verification
 * - Auto-repair via corridor carving between disconnected regions
 */
export class ValidationPass {
  /**
   * Run all validation checks and optional repairs.
   *
   * @param {Array<Array<number>>} grid — ground layer (mutated on repair)
   * @param {number} width
   * @param {number} height
   * @param {object} options
   * @param {number[]} options.floorTileIds — tile IDs considered walkable
   * @param {number}   [options.corridorTileId] — tile used to carve repair corridors
   * @param {boolean}  [options.repair=true] — auto-repair disconnected regions
   * @param {boolean}  [options.ensureExits=false] — guarantee at least one map-edge exit
   * @returns {{connected: boolean, regions: number, repaired: boolean, deadEnds: Array<{x:number,y:number}>}}
   */
  validate(grid, width, height, options = {}) {
    const {
      floorTileIds = [],
      corridorTileId,
      repair = true,
      ensureExits = false,
    } = options;

    const floorSet = new Set(floorTileIds);

    // 1. Find connected regions
    const { regions, regionMap } = this._findRegions(grid, width, height, floorSet);

    let connected = regions.length <= 1;
    let repaired = false;

    // 2. Repair if disconnected
    if (!connected && repair && corridorTileId !== undefined) {
      this.repairConnectivity(grid, width, height, floorSet, corridorTileId, regions);
      repaired = true;
      connected = true;
    }

    // 3. Dead ends
    const deadEnds = this.findDeadEnds(grid, width, height, floorSet);

    // 4. Exits
    if (ensureExits) {
      this.ensureExits(grid, width, height, floorSet, corridorTileId || floorTileIds[0]);
    }

    return {
      connected,
      regions: regions.length,
      repaired,
      deadEnds,
    };
  }

  /**
   * BFS flood-fill to identify all disconnected walkable regions.
   */
  _findRegions(grid, width, height, floorSet) {
    const visited = [];
    for (let y = 0; y < height; y++) visited.push(new Uint8Array(width));

    const regions = []; // array of arrays of {x, y}
    const regionMap = []; // same shape as grid, stores region index
    for (let y = 0; y < height; y++) regionMap.push(new Int16Array(width).fill(-1));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (visited[y][x]) continue;
        if (!floorSet.has(grid[y][x])) continue;

        const region = [];
        const queue = [[x, y]];
        visited[y][x] = 1;

        while (queue.length > 0) {
          const [cx, cy] = queue.pop();
          region.push({ x: cx, y: cy });
          regionMap[cy][cx] = regions.length;

          for (const [nx, ny] of [[cx - 1, cy], [cx + 1, cy], [cx, cy - 1], [cx, cy + 1]]) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            if (visited[ny][nx]) continue;
            if (!floorSet.has(grid[ny][nx])) continue;
            visited[ny][nx] = 1;
            queue.push([nx, ny]);
          }
        }

        regions.push(region);
      }
    }

    return { regions, regionMap };
  }

  /**
   * Connect all regions by carving straight corridors between closest
   * cell pairs of each region to the largest (main) region.
   */
  repairConnectivity(grid, width, height, floorSet, corridorTileId, regions) {
    if (regions.length <= 1) return;

    // Sort by size, connect smaller to largest
    regions.sort((a, b) => b.length - a.length);
    const main = regions[0];

    for (let i = 1; i < regions.length; i++) {
      // Find closest pair
      let bestDist = Infinity;
      let bestA = main[0];
      let bestB = regions[i][0];

      // Sample up to 50 cells from each region for perf
      const sampleA = main.length > 50 ? this._sample(main, 50) : main;
      const sampleB = regions[i].length > 50 ? this._sample(regions[i], 50) : regions[i];

      for (const a of sampleA) {
        for (const b of sampleB) {
          const dist = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
          if (dist < bestDist) {
            bestDist = dist;
            bestA = a;
            bestB = b;
          }
        }
      }

      // Carve L-shaped corridor
      this._carveLine(grid, bestA.x, bestA.y, bestB.x, bestA.y, corridorTileId);
      this._carveLine(grid, bestB.x, bestA.y, bestB.x, bestB.y, corridorTileId);
    }
  }

  /**
   * Find dead-end tiles: walkable cells with exactly 1 walkable neighbour.
   */
  findDeadEnds(grid, width, height, floorSet) {
    const deadEnds = [];
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        if (!floorSet.has(grid[y][x])) continue;
        let walkableNeighbors = 0;
        for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
          if (floorSet.has(grid[ny][nx])) walkableNeighbors++;
        }
        if (walkableNeighbors === 1) {
          deadEnds.push({ x, y });
        }
      }
    }
    return deadEnds;
  }

  /**
   * Ensure at least one walkable tile touches a map edge.
   * If none exists, carve from the nearest floor tile to the closest edge.
   */
  ensureExits(grid, width, height, floorSet, exitTileId) {
    // Check edges
    for (let x = 0; x < width; x++) {
      if (floorSet.has(grid[0][x]) || floorSet.has(grid[height - 1][x])) return;
    }
    for (let y = 0; y < height; y++) {
      if (floorSet.has(grid[y][0]) || floorSet.has(grid[y][width - 1])) return;
    }

    // No exit — find nearest floor tile to any edge
    let bestDist = Infinity;
    let bestCell = null;
    let bestEdge = null;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!floorSet.has(grid[y][x])) continue;
        const dists = [y, height - 1 - y, x, width - 1 - x];
        const minD = Math.min(...dists);
        if (minD < bestDist) {
          bestDist = minD;
          bestCell = { x, y };
          const idx = dists.indexOf(minD);
          if (idx === 0) bestEdge = { x, y: 0 };
          else if (idx === 1) bestEdge = { x, y: height - 1 };
          else if (idx === 2) bestEdge = { x: 0, y };
          else bestEdge = { x: width - 1, y };
        }
      }
    }

    if (bestCell && bestEdge) {
      this._carveLine(grid, bestCell.x, bestCell.y, bestEdge.x, bestEdge.y, exitTileId);
    }
  }

  // ---- helpers ----

  _carveLine(grid, x0, y0, x1, y1, tileId) {
    // Simple axis-aligned line
    const dx = x1 > x0 ? 1 : x1 < x0 ? -1 : 0;
    const dy = y1 > y0 ? 1 : y1 < y0 ? -1 : 0;

    let cx = x0, cy = y0;
    const maxSteps = Math.abs(x1 - x0) + Math.abs(y1 - y0) + 1;
    for (let i = 0; i < maxSteps; i++) {
      if (cy >= 0 && cy < grid.length && cx >= 0 && cx < grid[0].length) {
        grid[cy][cx] = tileId;
      }
      if (cx === x1 && cy === y1) break;
      if (cx !== x1) cx += dx;
      else cy += dy;
    }
  }

  _sample(arr, n) {
    if (arr.length <= n) return arr;
    const step = Math.floor(arr.length / n);
    const result = [];
    for (let i = 0; i < arr.length && result.length < n; i += step) {
      result.push(arr[i]);
    }
    return result;
  }
}
